import React, { useEffect, useState, useCallback } from 'react';
import { useAuth, UserDetails } from '../../../contexts/AuthContext';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { printReceipt as sendPrintReceipt } from '../../../services/localPrintService';

// Adisyon yazdırma servisi için tip tanımlamaları
interface PrintReceiptServiceProps {
  companyId: string | null;
  menuId: string | null;
  onFetchSettingsError?: (error: string) => void;
  onPrintError?: (error: string) => void;
  onPrintSuccess?: (message: string) => void;
  lazyLoad?: boolean; // Ayarların sadece ihtiyaç olduğunda yüklenmesi için
}

// QR Menü adisyon görüntü ayarları
interface ReceiptDisplaySettings {
  companyName: string;
  companyAddress: string;
  taxNumber: string;
  showReceiptHeader: boolean;
  showVat: boolean;
  showDiscountInfo: boolean;
  showCashierName: boolean;
  showReceiptNumber: boolean;
  receiptNumberResetValue: number;
  resetAtEndOfDay: boolean;
  printCopyCount: number; // Birden fazla kopya yazdırma sayısı
  productGrouping: string; 
  standardFooterNote: string;
  thankYouMessage: string;
  receiptWidth: number;
  characterLength: number;
  marginTop: number;
  marginBottom: number;
  marginLeft: number;
  marginRight: number;
  // Logo ayarları
  headerType?: 'text' | 'logo';
  logoUrl?: string;
}

// Yazdırılacak adisyon verisi için tip tanımlaması
export interface ReceiptDataItem {
  name: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  vatRate?: number;
  aciklama?: string; // ✅ Ürüne özel not (özelleştirme)
}

export interface ReceiptData {
  invoiceNumber: string;
  date: string;
  time: string;
  customerName: string;
  items: ReceiptDataItem[];
  subtotal: number;
  vatTotal: number;
  discountTotal: number;
  grandTotal: number;
  paymentMethod: string;
  cashierName: string;
  companyInfo: {
    name: string;
    address: string;
    taxNumber: string;
    phone: string;
  };
  showVatInfo: boolean;
  showReceiptHeader: boolean;
  showDiscountInfo: boolean;
  showCashierName: boolean;
  showReceiptNumber: boolean;
  receiptNumber?: number;
  resetAtEndOfDay?: boolean;
  printCopyCount?: number;
  productGrouping?: string;
  standardFooterNote?: string;
  thankYouMessage?: string;
  receiptFormat?: {
    receiptWidth?: number;
    characterLength?: number;
    marginTop?: number;
    marginBottom?: number;
    marginLeft?: number;
    marginRight?: number;
  };
  htmlTemplate?: string;
  orderNote?: string; // Sipariş notu için
  tableNumber?: number; // Masa numarası için
  headerType?: 'text' | 'logo'; // Başlık tipi
  logoUrl?: string; // Logo URL'si
}

// Yazıcı ve Adisyon Görüntü Ayarları için birleşik tip
interface MenuPrintSettings {
  definedPrinters?: { id: string; name: string }[];
  printingMode?: 'single' | 'category';
  defaultReceiptPrinter?: { id: string; name: string } | null;
  categoryPrinterAssignments?: { [categoryId: string]: string | null };
  receiptDisplaySettings?: ReceiptDisplaySettings;
}

// Sipariş öğesi tipi (KitchenScreen'den gelecek)
interface OrderItem {
  id: string;
  urunAdi: string;
  adet?: number;
  miktar?: number;
  birimFiyat: number;
  toplamFiyat: number;
  aciklama?: string;
  imageUrl?: string;
  urunResmi?: string;
}

interface PrintReceiptServiceReturn {
  printOrderReceipt: (orderData: {
    siparisId: string;
    masaNumarasi: number;
    kullaniciAdi: string;
    toplamTutar: number;
    siparisDetaylari: OrderItem[];
    siparisNotu?: string;
    siparisOlusturmaTarihi: string;
  }) => Promise<void>;
  printOrderReceiptByCategory: (orderData: {
    siparisId: string;
    masaNumarasi: number;
    kullaniciAdi: string;
    toplamTutar: number;
    siparisDetaylari: OrderItem[];
    siparisNotu?: string;
    siparisOlusturmaTarihi: string;
  }) => Promise<void>;
}

// Yerel yazıcı servisi endpoint
const LOCAL_SERVICE_BASE_URL = 'http://localhost:3003';
const LOCAL_SERVICE_URL = `${LOCAL_SERVICE_BASE_URL}/api`;

// Adisyon HTML şablonunu oluşturan fonksiyon
const createReceiptHtml = (receiptData: ReceiptData): string => {
  // Fiş numarası bölümü tamamen kaldırıldı

  // Adisyon HTML şablonunu oluştur
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Adisyon Yazdır</title>
      <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap" rel="stylesheet">
      <style>
        body {
          font-family: 'Poppins', sans-serif;
          font-size: 10pt;
          width: 80mm;
          margin: 0;
          padding: 2mm 5mm 5mm 5mm;
          box-sizing: border-box;
          line-height: 1.1;
        }
        .header {
          text-align: center;
          margin-bottom: 3px;
          padding-top: 0;
        }
        .header h2 {
          margin: 0;
          margin-bottom: 2px;
        }
        .header img {
          max-width: 60mm;
          max-height: 20mm;
          margin-bottom: 2px;
          display: block;
          margin-left: auto;
          margin-right: auto;
        }
        .company-info {
          text-align: center;
          margin-bottom: 3px;
          font-size: 9pt;
        }
        .company-info p {
          margin: 0 0 1px 0;
        }
        .invoice-details {
          margin-bottom: 3px;
          font-size: 9pt;
        }
        .invoice-details p {
          margin: 0 0 1px 0;
        }
        .items-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 8pt;
          margin-top: 2px;
          table-layout: fixed; /* Sabit tablo düzeni */
        }
        .items-table th, .items-table td {
          text-align: left;
          padding: 1px 2px;
          vertical-align: top;
        }
        .items-table th {
          border-bottom: 1px solid #ddd;
        }
        /* Sütun genişlikleri */
        .items-table th:nth-child(1), .items-table td:nth-child(1) {
          width: 50%; /* Ürün adı sütunu - %50 */
          word-wrap: break-word;
          word-break: break-word;
          hyphens: auto;
          line-height: 1.2;
        }
        .items-table th:nth-child(2), .items-table td:nth-child(2) {
          width: 15%; /* Miktar sütunu - %15 */
          text-align: right;
        }
        .items-table th:nth-child(3), .items-table td:nth-child(3) {
          width: 17.5%; /* Fiyat sütunu - %17.5 */
          text-align: right;
        }
        .items-table th:nth-child(4), .items-table td:nth-child(4) {
          width: 17.5%; /* Tutar sütunu - %17.5 */
          text-align: right;
        }
        /* Ürün açıklama satırı */
        .item-note {
          font-size: 8pt;
          padding-left: 15px;
          line-height: 1.2;
        }
        .summary {
          margin-top: 3px;
          text-align: right;
          font-size: 9pt;
        }
        .summary p {
          margin: 0 0 1px 0;
        }
        .footer {
          margin-top: 5px;
          text-align: center;
          font-size: 9pt;
          border-top: 1px dashed #ddd;
          padding-top: 3px;
        }
        .footer p {
          margin: 0 0 1px 0;
        }
        .bold {
          font-weight: bold;
        }
        .center {
          text-align: center;
        }
        .right {
          text-align: right;
        }
        .order-note {
          background-color: #fff3cd;
          border: 1px solid #ffeaa7;
          padding: 5px;
          margin: 5px 0;
          border-radius: 3px;
          font-style: italic;
        }
      </style>
    </head>
    <body>
      <div class="header">
        ${receiptData.headerType === 'logo' && receiptData.logoUrl ? 
          `<img src="${receiptData.logoUrl}" alt="Logo" crossorigin="anonymous" />` : 
          `<h2>${receiptData.companyInfo?.name || 'Restoran'}</h2>`
        }
      </div>
      
      ${receiptData.headerType === 'logo' && receiptData.logoUrl ? '' : `
        <div class="company-info">
          ${receiptData.companyInfo?.address ? `<p>${receiptData.companyInfo.address}</p>` : ''}
          ${receiptData.companyInfo?.phone ? `<p>Tel: ${receiptData.companyInfo.phone}</p>` : ''}
          ${receiptData.companyInfo?.taxNumber ? `<p>VKN: ${receiptData.companyInfo.taxNumber}</p>` : ''}
        </div>
      `}
      
      
      <div class="invoice-details">
        <p><span class="bold">Sipariş No:</span> ${receiptData.invoiceNumber}</p>
        <p><span class="bold">Tarih:</span> ${receiptData.date} ${receiptData.time}</p>
        ${receiptData.tableNumber ? `<p><span class="bold">Masa:</span> ${receiptData.tableNumber}</p>` : ''}
        <p><span class="bold">Müşteri:</span> ${receiptData.customerName}</p>
      </div>
      
      ${receiptData.orderNote ? `
        <div class="order-note">
          <p><span class="bold">📝 Sipariş Notu:</span></p>
          <p>"${receiptData.orderNote}"</p>
        </div>
      ` : ''}
      
      <table class="items-table">
        <thead>
          <tr>
            <th>Ürün</th>
            <th class="right">Miktar</th>
            <th class="right">Fiyat</th>
            <th class="right">Tutar</th>
          </tr>
        </thead>
        <tbody>
          ${receiptData.items.map(item => `
            <tr>
              <td>${item.name}</td>
              <td class="right">${item.quantity}</td>
              <td class="right">${item.unitPrice.toFixed(2)}</td>
              <td class="right">${item.totalPrice.toFixed(2)}</td>
            </tr>
            ${item.aciklama ? `
              <tr>
                <td colspan="4" class="item-note">Not: ${item.aciklama}</td>
              </tr>
            ` : ''}
          `).join('')}
        </tbody>
      </table>
      
      <div class="summary">
        ${receiptData.discountTotal > 0 ? `<p>İndirim: ${receiptData.discountTotal.toFixed(2)} TL</p>` : ''}
        ${receiptData.showVatInfo ? `<p>Ara Toplam: ${receiptData.subtotal.toFixed(2)} TL</p>` : ''}
        ${receiptData.showVatInfo ? `<p>KDV Toplam: ${receiptData.vatTotal.toFixed(2)} TL</p>` : ''}
        <p class="bold">Genel Toplam: ${receiptData.grandTotal.toFixed(2)} TL</p>
      </div>
      
      <div class="footer">
        ${receiptData.standardFooterNote ? `<p>${receiptData.standardFooterNote}</p>` : ''}
        ${receiptData.thankYouMessage ? `<p>${receiptData.thankYouMessage}</p>` : ''}
      </div>
    </body>
    </html>
  `;
};

// Test sayfası yazdırma fonksiyonu
export const printTestPage = async (printerName: string): Promise<void> => {
  try {
    const htmlTemplate = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Yazıcı Sınama Sayfası</title>
        <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap" rel="stylesheet">
        <style>
          body {
            font-family: 'Poppins', sans-serif;
            font-size: 10pt;
            width: 80mm;
            margin: 0;
            padding: 2mm 5mm 5mm 5mm;
            box-sizing: border-box;
            line-height: 1.1;
          }
          .header {
            text-align: center;
            border-bottom: 1px solid black;
            padding-bottom: 10px;
            margin-bottom: 10px;
          }
          .info {
            margin-bottom: 15px;
          }
          .test-area {
            border: 1px solid black;
            padding: 10px;
            margin-bottom: 15px;
          }
          .footer {
            text-align: center;
            border-top: 1px solid black;
            padding-top: 10px;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>YAZICI SINAMA SAYFASI</h1>
          <p>QR Menü Adisyon Servisi</p>
        </div>
        
        <div class="info">
          <p><strong>Yazıcı:</strong> ${printerName}</p>
          <p><strong>Tarih:</strong> ${new Date().toLocaleString('tr-TR')}</p>
        </div>
        
        <div class="test-area">
          <h3>KARAKTER TESTİ</h3>
          <p>ABCDEFGHJKLMNOPQRSTUVWXYZ</p>
          <p>abcdefghijklmnopqrstuvwxyz</p>
          <p>0123456789</p>
          <p>!@#$%^&*()_+-=</p>
        </div>
        
        <div class="test-area">
          <h3>TÜRKÇE KARAKTER TESTİ</h3>
          <p>çÇ ğĞ ıİ öÖ şŞ üÜ</p>
          <p>ÖZEL TÜRKÇE KARAKTERLER</p>
          <p>Şükrü Şahin</p>
          <p>Güzel çiçekler</p>
          <p>Ölçü İğnesi</p>
          <p>Ünlü Şapkası</p>
        </div>
        
        <div class="footer">
          <p>Bu bir sınama sayfasıdır.</p>
          <p>Eğer bu metni net şekilde okuyabiliyorsanız,</p>
          <p>yazıcınız başarıyla yapılandırılmıştır.</p>
        </div>
      </body>
      </html>
    `;
    
    await axios.post(`${LOCAL_SERVICE_URL}/sinamaSayfasiYazdir`, {
      printerName,
      htmlTemplate
    });
    // Sınama sayfası gönderildi
    return Promise.resolve();
  } catch (error: any) {
    // Sınama sayfası yazdırma hatası
    return Promise.reject(error);
  }
};

export const usePrintReceipt = ({
  companyId,
  menuId,
  onFetchSettingsError,
  onPrintError,
  onPrintSuccess,
  lazyLoad = true
}: PrintReceiptServiceProps): PrintReceiptServiceReturn => {
  const { t } = useTranslation('salesSystem');
  const { userDetails } = useAuth();
  const [menuSettings, setMenuSettings] = useState<MenuPrintSettings | null>(null);
  const [currentReceiptNumber, setCurrentReceiptNumber] = useState<number>(1);
  const [receiptNumberLoaded, setReceiptNumberLoaded] = useState<boolean>(false);
  
  // Menü yazdırma ayarlarını localStorage'dan çek
  const fetchMenuSettings = useCallback(async (): Promise<MenuPrintSettings | null> => {
    if (!menuId) {
      // Menü ID bulunamadı
      onFetchSettingsError?.(t('printReceiptService.errors.menuIdNotFound'));
      return null;
    }
    
    try {
      const printingSettingsKey = `qrMenuPrintingSettings_${menuId}`;
      const storedSettings = localStorage.getItem(printingSettingsKey);
      
      if (storedSettings) {
        const data = JSON.parse(storedSettings) as MenuPrintSettings;
        setMenuSettings(data);
        return data;
      } else {
        // Varsayılan ayarlar kullanılıyor
        
        // Varsayılan ayarlar
        const defaultSettings: MenuPrintSettings = {
          printingMode: 'single',
          defaultReceiptPrinter: null,
          definedPrinters: [],
          categoryPrinterAssignments: {},
          receiptDisplaySettings: {
            companyName: 'Restoran',
            companyAddress: '',
            taxNumber: '',
            showReceiptHeader: true,
            showVat: true,
            showDiscountInfo: false,
            showCashierName: false, // Kasiyer bölümü kapalı
            showReceiptNumber: false, // Fiş sırası kapalı
            receiptNumberResetValue: 999999,
            resetAtEndOfDay: true,
            printCopyCount: 1,
            productGrouping: 'category',
            standardFooterNote: 'Afiyet olsun!',
            thankYouMessage: 'Tekrar bekleriz.',
            receiptWidth: 80,
            characterLength: 32,
            marginTop: 2,
            marginBottom: 5,
            marginLeft: 5,
            marginRight: 5,
            headerType: 'text', // Varsayılan olarak metin başlık
            logoUrl: ''
          }
        };
        
        setMenuSettings(defaultSettings);
        return defaultSettings;
      }
    } catch (error: any) {
      // localStorage okuma hatası
      onFetchSettingsError?.(t('printReceiptService.errors.printSettingsLoadFailed', { message: error.message }));
      return null;
    }
  }, [menuId, onFetchSettingsError, t]);

  // Fiş numarasını localStorage'dan yükleyen fonksiyon
  const loadReceiptNumber = useCallback(() => {
    // Adisyon numarası yükleniyor
    const storedReceiptNumber = localStorage.getItem(`qrMenuReceiptNumber_${menuId}`);
    const storedReceiptDate = localStorage.getItem(`qrMenuReceiptDate_${menuId}`);
    const todayDate = new Date().toISOString().split('T')[0];
    
    if (storedReceiptNumber) {
      const numericValue = parseInt(storedReceiptNumber, 10);
      setCurrentReceiptNumber(numericValue);
      // Adisyon numarası yüklendi
    } else {
      setCurrentReceiptNumber(1);
      localStorage.setItem(`qrMenuReceiptNumber_${menuId}`, '1');
      localStorage.setItem(`qrMenuReceiptDate_${menuId}`, todayDate);
      // Adisyon numarası 1 olarak ayarlandı
    }
    
    setReceiptNumberLoaded(true);
    return Promise.resolve();
  }, [menuId]);

  // Fiş numarası yönetimi için hook
  useEffect(() => {
    if (menuSettings && menuSettings.receiptDisplaySettings && menuSettings.receiptDisplaySettings.showReceiptNumber) {
      const storedReceiptNumber = localStorage.getItem(`qrMenuReceiptNumber_${menuId}`);
      const storedReceiptDate = localStorage.getItem(`qrMenuReceiptDate_${menuId}`);
      const todayDate = new Date().toISOString().split('T')[0];
      
      if (menuSettings.receiptDisplaySettings.resetAtEndOfDay && storedReceiptDate && storedReceiptDate !== todayDate) {
        setCurrentReceiptNumber(1);
        localStorage.setItem(`qrMenuReceiptNumber_${menuId}`, '1');
        localStorage.setItem(`qrMenuReceiptDate_${menuId}`, todayDate);
        setReceiptNumberLoaded(true);
      } else if (storedReceiptNumber) {
        setCurrentReceiptNumber(parseInt(storedReceiptNumber, 10));
        setReceiptNumberLoaded(true);
      } else {
        setCurrentReceiptNumber(1);
        localStorage.setItem(`qrMenuReceiptNumber_${menuId}`, '1');
        localStorage.setItem(`qrMenuReceiptDate_${menuId}`, todayDate);
        setReceiptNumberLoaded(true);
      }
    }
  }, [menuSettings, menuId]);

  // Başlangıçta adisyon numarasını yükle
  useEffect(() => {
    loadReceiptNumber();
  }, [loadReceiptNumber]);

  // Sonraki adisyon numarasını al ve kaydet
  const getNextReceiptNumber = useCallback(() => {
    const storedReceiptNumber = localStorage.getItem(`qrMenuReceiptNumber_${menuId}`);
    const currentNumber = storedReceiptNumber ? parseInt(storedReceiptNumber, 10) : currentReceiptNumber;
    
    const resetValue = menuSettings?.receiptDisplaySettings?.receiptNumberResetValue || 999999;
    
    let newReceiptNumber = currentNumber + 1;
    
    if (newReceiptNumber > resetValue) {
      newReceiptNumber = 1;
    }
    
    localStorage.setItem(`qrMenuReceiptNumber_${menuId}`, newReceiptNumber.toString());
    
    const todayDate = new Date().toISOString().split('T')[0];
    localStorage.setItem(`qrMenuReceiptDate_${menuId}`, todayDate);
    
    // Adisyon numarası kullanılıyor
    
    return currentNumber;
  }, [menuSettings, currentReceiptNumber, menuId]);

  // İlk yüklendiğinde ayarları çek (lazyLoad=false ise)
  useEffect(() => {
    if (!lazyLoad && menuId) {
      fetchMenuSettings().catch(err => {
        // Yazdırma ayarları yükleme hatası
      });
    }
  }, [menuId, lazyLoad]); // companyId dependency'sini kaldırdık

  // Sipariş adisyonunu yazdırma fonksiyonu
  // Kategori bazlı yazdırma fonksiyonu
  const printOrderReceiptByCategory = useCallback(async (orderData: {
    siparisId: string;
    masaNumarasi: number;
    kullaniciAdi: string;
    toplamTutar: number;
    siparisDetaylari: OrderItem[];
    siparisNotu?: string;
    siparisOlusturmaTarihi: string;
  }) => {
    let settings = menuSettings;
    if (!settings) {
      settings = await fetchMenuSettings();
      if (!settings) { 
        onPrintError?.(t('printReceiptService.errors.printSettingsLoadFailedShort')); 
        return; 
      }
    }

    // Kategori bazlı yazdırma kontrolü
    if (settings.printingMode !== 'category') {
      // Tek yazıcı modunda normal yazdırma yap
      return printOrderReceipt(orderData);
    }

    // Ürünleri kategorilerine göre grupla
    const itemsByCategory: { [categoryId: string]: OrderItem[] } = {};
    
    orderData.siparisDetaylari.forEach(item => {
      // Farklı alan isimlerini kontrol et
      const categoryId = (item as any).categoryId || 
                        (item as any).categoryName || 
                        (item as any).kategori || 
                        'Diğer';
      
      if (!itemsByCategory[categoryId]) {
        itemsByCategory[categoryId] = [];
      }
      itemsByCategory[categoryId].push(item);
    });

    // Yazıcıya göre ürünleri ve yazıcı bilgilerini grupla
    const itemsByPrinter = new Map<string, { printer: { id: string; name: string }; items: OrderItem[] }>();

    for (const [categoryId, items] of Object.entries(itemsByCategory)) {
      const assignedPrinterId = settings.categoryPrinterAssignments?.[categoryId];
      
      if (assignedPrinterId) {
        const assignedPrinter = settings.definedPrinters?.find(p => p.id === assignedPrinterId);
        if (assignedPrinter) {
          if (itemsByPrinter.has(assignedPrinterId)) {
            // Mevcut yazıcıya ürünleri ekle
            itemsByPrinter.get(assignedPrinterId)!.items.push(...items);
          } else {
            // Yeni yazıcı için giriş oluştur
            itemsByPrinter.set(assignedPrinterId, { printer: assignedPrinter, items: [...items] });
          }
        }
      }
    }

    console.log('🖨️ Yazıcı bazlı yazdırma - Gruplanan yazıcılar:', Array.from(itemsByPrinter.keys()));
    
    // Her yazıcı için ayrı yazdırma yap
    const printPromises: Promise<void>[] = [];
    
    for (const { printer, items } of Array.from(itemsByPrinter.values())) {
      console.log(`🖨️ ${printer.name} yazıcısı için ${items.length} ürün hazırlanıyor...`);

      // Bu yazıcı için sipariş verisi oluştur
      const printerOrderData = {
        ...orderData,
        siparisDetaylari: items
      };

      // Bu yazıcı için yazdırma promise'i oluştur
      const printPromise = printSingleCategoryReceipt(printerOrderData, printer, settings);
      printPromises.push(printPromise);
    }

    // Yazdırma işlemlerini sıralı olarak yap
    if (printPromises.length > 0) {
      try {
        for (const printPromise of printPromises) {
          await printPromise;
          await new Promise(resolve => setTimeout(resolve, 250)); 
        }
        console.log('✅ Tüm kategori yazdırmaları tamamlandı');
        onPrintSuccess?.(t('printReceiptService.success.categoryPrinted', { count: printPromises.length }));
      } catch (error: any) {
        console.error('❌ Kategori yazdırma hatası:', error);
        onPrintError?.(error.message || t('printReceiptService.errors.categoryPrintFailed'));
      }
    } else {
      console.log('⚠️ Hiçbir kategori için yazıcı atanmamış');
      onPrintError?.(t('printReceiptService.errors.noPrinterAssigned'));
    }
  }, [menuSettings, onPrintError, onPrintSuccess, userDetails, t]);

  // Tek kategori için yazdırma fonksiyonu
  const printSingleCategoryReceipt = async (
    orderData: {
      siparisId: string;
      masaNumarasi: number;
      kullaniciAdi: string;
      toplamTutar: number;
      siparisDetaylari: OrderItem[];
      siparisNotu?: string;
      siparisOlusturmaTarihi: string;
    }, 
    printer: { id: string; name: string },
    settings: MenuPrintSettings
  ): Promise<void> => {
    const rds = settings.receiptDisplaySettings;
    
    // Adisyon numarası gösterilecekse ve adisyon numarası yüklenmemişse, önce yükle
    if (rds?.showReceiptNumber && !receiptNumberLoaded) {
      await loadReceiptNumber();
    }

    // Kategori ürünlerini dönüştür
    const transformedItems: ReceiptDataItem[] = orderData.siparisDetaylari.map(item => {
      const quantity = item.adet || item.miktar || 1;
      const itemTotalPrice = item.toplamFiyat;
      const vatRate = 18;
      
      return {
        name: item.urunAdi,
        quantity: quantity,
        unitPrice: item.birimFiyat,
        totalPrice: itemTotalPrice,
        vatRate,
        aciklama: item.aciklama || undefined // ✅ Ürüne özel not
      };
    });

    // Kategori toplamını hesapla
    const categoryTotal = transformedItems.reduce((sum, item) => sum + item.totalPrice, 0);
    const categoryVatTotal = transformedItems.reduce((sum, item) => sum + (item.totalPrice * ((item.vatRate || 18) / 100)), 0);

    // Kategori adisyon verilerini oluştur
    const categoryReceiptData: ReceiptData = {
      invoiceNumber: orderData.siparisId,
      date: new Date(orderData.siparisOlusturmaTarihi).toLocaleDateString('tr-TR'),
      time: new Date(orderData.siparisOlusturmaTarihi).toLocaleTimeString('tr-TR'),
      customerName: orderData.kullaniciAdi,
      cashierName: userDetails?.firstName && userDetails?.lastName 
        ? `${userDetails.firstName} ${userDetails.lastName}` 
        : 'Sistem',
      paymentMethod: 'Nakit', // Varsayılan ödeme yöntemi
      companyInfo: {
        name: rds?.companyName || 'Restoran',
        address: rds?.companyAddress || '',
        taxNumber: rds?.taxNumber || '',
        phone: '' // Eksik phone alanını ekle
      },
      showReceiptHeader: rds?.showReceiptHeader || true,
      showVatInfo: rds?.showVat || false,
      showDiscountInfo: rds?.showDiscountInfo || false,
      showCashierName: rds?.showCashierName || false,
      showReceiptNumber: rds?.showReceiptNumber || false,
      items: transformedItems,
      subtotal: categoryTotal,
      vatTotal: categoryVatTotal,
      discountTotal: 0,
      grandTotal: categoryTotal,
      // Eksik alanlar eklendi
      orderNote: orderData.siparisNotu,
      tableNumber: orderData.masaNumarasi,
      headerType: rds?.headerType || 'text',
      logoUrl: rds?.logoUrl || '',
      standardFooterNote: rds?.standardFooterNote,
      thankYouMessage: rds?.thankYouMessage,
      printCopyCount: rds?.printCopyCount || 1,
      receiptFormat: rds ? {
        receiptWidth: rds.receiptWidth,
        characterLength: rds.characterLength,
        marginTop: rds.marginTop,
        marginBottom: rds.marginBottom,
        marginLeft: rds.marginLeft,
        marginRight: rds.marginRight,
      } : undefined,
    };
    
    // HTML şablonunu oluştur
    const htmlContent = createReceiptHtml(categoryReceiptData);
    
    // HTML şablonunu veriye ekle
    categoryReceiptData.htmlTemplate = htmlContent;
    
    // Yazdırma isteği gönder
    console.log(`[PrintReceiptService] Kategori adisyonu yazdırma isteği gönderiliyor: ${printer.name}`);

    try {
      await sendPrintReceipt({
        printerName: printer.name,
        receiptData: categoryReceiptData
      }, { baseUrl: LOCAL_SERVICE_BASE_URL });

      console.log(`✅ Kategori adisyonu başarıyla yazdırıldı: ${printer.name}`);
    } catch (error: any) {
      console.error(`❌ Kategori yazdırma hatası (${printer.name}):`, error);
      throw error;
    }
  };

  const printOrderReceipt = useCallback(async (orderData: {
    siparisId: string;
    masaNumarasi: number;
    kullaniciAdi: string;
    toplamTutar: number;
    siparisDetaylari: OrderItem[];
    siparisNotu?: string;
    siparisOlusturmaTarihi: string;
  }) => {
    let settings = menuSettings;
    if (!settings) {
      settings = await fetchMenuSettings();
      if (!settings) { 
        onPrintError?.(t('printReceiptService.errors.printSettingsLoadFailedShort')); 
        return; 
      }
    }

    const rds = settings.receiptDisplaySettings;
    
    // Adisyon numarası gösterilecekse ve adisyon numarası yüklenmemişse, önce yükle
    if (rds?.showReceiptNumber && !receiptNumberLoaded) {
      // Adisyon numarası yükleniyor
      await loadReceiptNumber();
    }
    
    if (rds?.showReceiptNumber && !receiptNumberLoaded) {
      // Adisyon numarası yüklenemedi
      onPrintError?.(t('printReceiptService.errors.receiptNumberLoadFailed'));
      return;
    }

    // Adisyon verilerini hazırla
    const constructFullReceiptData = (): ReceiptData => {
      let calculatedVatTotal = 0;

      const transformedItems: ReceiptDataItem[] = orderData.siparisDetaylari.map(item => {
        const quantity = item.adet || item.miktar || 1;
        const itemTotalPrice = item.toplamFiyat;
        const vatRate = 18; // Varsayılan KDV oranı
        
        const itemModel: ReceiptDataItem = {
          name: item.urunAdi,
          quantity: quantity,
          unitPrice: item.birimFiyat,
          totalPrice: itemTotalPrice,
          vatRate,
          aciklama: item.aciklama || undefined // ✅ Ürüne özel not
        };
        
        calculatedVatTotal += (itemTotalPrice * (vatRate / 100));
        
        return itemModel;
      });
      
      // Adisyon numarası ayarlarını kontrol et
      let receiptNumber: number | undefined = undefined;
      if (rds?.showReceiptNumber) {
        if (!receiptNumberLoaded || currentReceiptNumber <= 0) {
          const storedNumber = localStorage.getItem(`qrMenuReceiptNumber_${menuId}`);
          if (storedNumber) {
            receiptNumber = parseInt(storedNumber, 10);
            setCurrentReceiptNumber(receiptNumber);
          } else {
            receiptNumber = 1;
            setCurrentReceiptNumber(1);
            localStorage.setItem(`qrMenuReceiptNumber_${menuId}`, '1');
          }
        } else {
          receiptNumber = getNextReceiptNumber();
        }
        
        // Adisyon numarası alındı
      }

      const orderDate = new Date(orderData.siparisOlusturmaTarihi);
      
      const fullData: ReceiptData = {
        invoiceNumber: orderData.siparisId,
        date: orderDate.toLocaleDateString('tr-TR'),
        time: orderDate.toLocaleTimeString('tr-TR'),
        customerName: orderData.kullaniciAdi,
        items: transformedItems,
        subtotal: orderData.toplamTutar,
        vatTotal: calculatedVatTotal,
        discountTotal: 0,
        grandTotal: orderData.toplamTutar,
        paymentMethod: 'Nakit', // Varsayılan
        cashierName: userDetails ? `${userDetails.firstName} ${userDetails.lastName}`.trim() : '',
        companyInfo: {
          name: rds?.companyName || 'Restoran',
          address: rds?.companyAddress || '',
          taxNumber: rds?.taxNumber || '',
          phone: ''
        },
        showVatInfo: rds?.showVat || false,
        showReceiptHeader: rds?.showReceiptHeader || false,
        showDiscountInfo: rds?.showDiscountInfo || false,
        showCashierName: rds?.showCashierName || false,
        showReceiptNumber: rds?.showReceiptNumber || false,
        receiptNumber: receiptNumber,
        resetAtEndOfDay: rds?.resetAtEndOfDay,
        printCopyCount: rds?.printCopyCount || 1,
        productGrouping: rds?.productGrouping,
        standardFooterNote: rds?.standardFooterNote,
        thankYouMessage: rds?.thankYouMessage,
        receiptFormat: rds ? {
          receiptWidth: rds.receiptWidth,
          characterLength: rds.characterLength,
          marginTop: rds.marginTop,
          marginBottom: rds.marginBottom,
          marginLeft: rds.marginLeft,
          marginRight: rds.marginRight,
        } : undefined,
        orderNote: orderData.siparisNotu,
        tableNumber: orderData.masaNumarasi,
        headerType: rds?.headerType || 'text',
        logoUrl: rds?.logoUrl || ''
      };
      
      fullData.htmlTemplate = createReceiptHtml(fullData);
      
      // Fiş verisi oluşturuldu
      
      return fullData;
    };

    if (settings.printingMode === 'single') {
      if (!settings.defaultReceiptPrinter) { 
        onPrintError?.(t('printReceiptService.errors.defaultPrinterNotSet')); 
        return; 
      }
      
      const fullReceiptData = constructFullReceiptData();
      try {
        // Adisyon yazdırma isteği gönderiliyor
        
        await sendPrintReceipt({
          printerId: settings.defaultReceiptPrinter.id,
          printerName: settings.defaultReceiptPrinter.name,
          receiptData: fullReceiptData
        }, { baseUrl: LOCAL_SERVICE_BASE_URL });
        onPrintSuccess?.(t('printReceiptService.success.receiptPrinted'));
      } catch (error: any) {
        // Yazdırma hatası
        onPrintError?.(t('printReceiptService.errors.receiptPrintFailed', { message: error.message }));
      }
    } else {
      // Kategori bazlı yazdırma - şimdilik tek yazıcı gibi davran
      if (!settings.defaultReceiptPrinter) { 
        onPrintError?.(t('printReceiptService.errors.defaultPrinterNotSet')); 
        return; 
      }
      
      const fullReceiptData = constructFullReceiptData();
      try {
        await sendPrintReceipt({
          printerId: settings.defaultReceiptPrinter.id,
          printerName: settings.defaultReceiptPrinter.name,
          receiptData: fullReceiptData
        }, { baseUrl: LOCAL_SERVICE_BASE_URL });
        onPrintSuccess?.(t('printReceiptService.success.receiptPrinted'));
      } catch (error: any) {
        // Yazdırma hatası
        onPrintError?.(t('printReceiptService.errors.receiptPrintFailed', { message: error.message }));
      }
    }
  }, [menuSettings, companyId, menuId, onPrintError, onPrintSuccess, fetchMenuSettings, userDetails, receiptNumberLoaded, loadReceiptNumber, getNextReceiptNumber, t]);

  return { printOrderReceipt, printOrderReceiptByCategory };
};

// Geriye uyumluluk için ayrıca bir bileşen olarak da dışa aktarabiliriz
const PrintReceiptService: React.FC<PrintReceiptServiceProps & { children?: React.ReactNode }> = (props) => {
  usePrintReceipt(props);
  return null;
};

export default PrintReceiptService;
